import { useState, useEffect, useCallback, useRef } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { AnalyticsEvents } from '@pantry/analytics';
import { logger } from '../../lib/logger';
import { useAnalytics } from '../../hooks/useAnalytics';
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Label,
} from '@pantry/ui';
import { cn } from '@pantry/ui';
import {
  useFindInventoryItemByBarcode,
  useBulkUpdateInstances,
  useBulkCreateItems,
  useSearchProducts,
  useResetTour,
  type ItemInstance,
  type ItemWithInstanceCounts,
  type BulkUpdateItem,
  type BulkCreateItem,
  type ConsumeReason,
  type WasteReason,
  type ItemUnit,
  CONSUME_REASONS,
  CONSUME_REASON_LABELS,
  WASTE_REASONS,
  WASTE_REASON_LABELS,
  UNIT_DISPLAY_LABELS,
  formatQuantityDisplay,
  getQuickSelectOptions,
  getQuantityStepSize,
  getUnitCategory,
  convertQuantity,
} from '@pantry/api-client';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';
import { useMealPrepStore } from '@pantry/shared-react/stores';
import { useMealPrepSelection } from '@pantry/shared-react/hooks';
import { SelectedItemCard } from '../../components/meal-prep/selected-item-card';
import { useTourContext } from '../../components/onboarding/useTourContext';
import { ContinuousBarcodeScanner } from '../../components/barcode/ContinuousBarcodeScanner';
import { InventoryPickerSheet, InstancePickerSheet } from '../../components/meal-prep';
import { PendingItemCard } from '../../components/meal-prep/PendingItemCard';
import { UnitSelector } from '../../components/items/unit-selector';
import { toast } from 'sonner';
import {
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  BeakerIcon,
  ArrowLeftIcon,
  QrCodeIcon,
  ClipboardDocumentListIcon,
  TrashIcon,
  FireIcon,
  SparklesIcon,
  UsersIcon,
  GiftIcon,
  ClockIcon,
  NoSymbolIcon,
  QuestionMarkCircleIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

export const Route = createFileRoute('/_app/meal-prep')({
  component: MealPrepPage,
});

type PageStep = 'selecting' | 'confirm-action' | 'processing' | 'success';
type ConfirmSubStep = 'adjust-quantities' | 'select-reason';

/** Track quantities to consume for each instance */
type QuantityMap = Map<string, number>;

/** Track display units for each instance (for unit switching) */
type UnitMap = Map<string, ItemUnit>;

/** Track max quantities in display units for each instance */
type MaxQuantityMap = Map<string, number>;

type MealPrepMiniTourId = 'meal-prep-basics' | 'meal-prep-actions';

/**
 * Get icon for consume reason
 */
function getConsumeReasonIcon(reason: ConsumeReason) {
  switch (reason) {
    case 'cooked':
      return FireIcon;
    case 'eaten_raw':
      return SparklesIcon;
    case 'shared':
      return UsersIcon;
    case 'donated':
      return GiftIcon;
    case 'other':
    default:
      return BeakerIcon;
  }
}

/**
 * Get icon for waste reason
 */
function getWasteReasonIcon(reason: WasteReason) {
  switch (reason) {
    case 'expired':
      return ClockIcon;
    case 'spoiled':
      return ExclamationTriangleIcon;
    case 'damaged':
      return NoSymbolIcon;
    case 'other':
    default:
      return QuestionMarkCircleIcon;
  }
}

const DEMO_MEAL_PREP_ITEMS = [
  { id: 'demo-meal-prep-1', name: 'Chicken Breast', quantity: '2 items' },
  { id: 'demo-meal-prep-2', name: 'Bell Pepper', quantity: '3 items' },
  { id: 'demo-meal-prep-3', name: 'Olive Oil', quantity: '50 ml' },
] as const;

function MealPrepPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { selectedHousehold, isLoading: householdLoading } = useSelectedHousehold();
  const householdId = selectedHousehold?.id || '';
  const { track } = useAnalytics();
  const { startTour, isActive: isTourActive, currentTourId } = useTourContext();
  const resetTour = useResetTour();
  const startTimeRef = useRef<number>(Date.now());
  const hasTrackedStartRef = useRef(false);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    };
  }, []);

  // UI state
  const [showScanner, setShowScanner] = useState(false);
  const [showInventoryPicker, setShowInventoryPicker] = useState(false);
  const [showInstancePicker, setShowInstancePicker] = useState(false);
  const [pickerItem, setPickerItem] = useState<ItemWithInstanceCounts | null>(null);
  const [pickerInstances, setPickerInstances] = useState<ItemInstance[]>([]);
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [step, setStep] = useState<PageStep>('selecting');
  const [confirmSubStep, setConfirmSubStep] = useState<ConfirmSubStep>('adjust-quantities');
  const [actionType, setActionType] = useState<'consumed' | 'wasted' | null>(null);
  const [selectedReason, setSelectedReason] = useState<ConsumeReason | WasteReason | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Track quantities to consume/waste for each instance (instanceId -> quantity)
  const [itemQuantities, setItemQuantities] = useState<QuantityMap>(new Map());
  // Track display units for each instance (for unit switching)
  const [displayUnits, setDisplayUnits] = useState<UnitMap>(new Map());
  // Track max quantities in display units for each instance
  const [maxInDisplayUnits, setMaxInDisplayUnits] = useState<MaxQuantityMap>(new Map());
  const [startingMiniTourId, setStartingMiniTourId] = useState<MealPrepMiniTourId | null>(null);

  // Store state
  const {
    activate,
    deactivate,
    toggleSelection,
    clearSelection,
    getSelectionCount,
    getItemIdForInstance,
    addPendingItem,
    updatePendingItem,
    removePendingItem,
    getPendingItems,
    getPendingItemsCount,
    areAllPendingItemsValid,
  } = useMealPrepStore();
  const selectionCount = getSelectionCount();
  const pendingItemsCount = getPendingItemsCount();
  const totalCount = selectionCount + pendingItemsCount;
  const pendingItems = getPendingItems();
  const isMealPrepActionsTourActive = isTourActive && currentTourId === 'meal-prep-actions';
  const isMealPrepActionsTourStarting = startingMiniTourId === 'meal-prep-actions';
  const shouldShowGhostMealPrepItems =
    (isMealPrepActionsTourActive || isMealPrepActionsTourStarting) && totalCount === 0;
  const shouldShowGhostActionBar =
    (isMealPrepActionsTourActive || isMealPrepActionsTourStarting) && totalCount === 0;
  const displayActionBarCount = shouldShowGhostActionBar ? DEMO_MEAL_PREP_ITEMS.length : totalCount;

  // Live selection data from React Query
  const { selectedItems } = useMealPrepSelection(householdId);

  // Barcode lookup for scanned barcodes - checks inventory
  const { data: barcodeResult, isFetching: barcodeFetching } = useFindInventoryItemByBarcode(
    householdId,
    scannedBarcode
  );

  // Product search state for unknown barcodes
  const [productSearchBarcode, setProductSearchBarcode] = useState('');

  // Product search query - for looking up unknown barcodes in OFF/database
  const { data: productSearchResult, isFetching: productSearchFetching } = useSearchProducts(
    householdId,
    { barcode: productSearchBarcode },
    { enabled: !!productSearchBarcode && !!householdId }
  );

  // Bulk mutations
  const bulkUpdate = useBulkUpdateInstances();
  const bulkCreate = useBulkCreateItems();

  // Activate meal prep mode on mount, deactivate on unmount
  useEffect(() => {
    activate();
    startTimeRef.current = Date.now();
    return () => {
      deactivate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track meal prep started (only once per session)
  useEffect(() => {
    if (householdId && !hasTrackedStartRef.current) {
      hasTrackedStartRef.current = true;
      track(AnalyticsEvents.MEAL_PREP_STARTED, {
        household_id: householdId,
      });
    }
  }, [householdId, track]);

  // Handle barcode result when it arrives - found in inventory
  useEffect(() => {
    if (!scannedBarcode || barcodeFetching) return;

    // Not in inventory - trigger product search for OFF/database lookup
    if (!barcodeResult?.product) {
      setProductSearchBarcode(scannedBarcode);
      setScannedBarcode('');
      return;
    }

    // Product known but no active stock - create pending item for "add and use"
    if (barcodeResult.instances.length === 0) {
      const product = barcodeResult.product;
      addPendingItem({
        barcode: scannedBarcode,
        productId: product.id,
        productName: product.name,
        productBrand: product.brand || undefined,
        productImageUrl: product.imageUrl || undefined,
        quantity: product.defaultQuantity || 1,
        unit: product.defaultUnit || 'item',
      });
      toast.success(t`${product.name} added - set quantity and storage location`);
      setScannedBarcode('');
      return;
    }

    if (barcodeResult.instances.length === 1) {
      // Single instance - auto-select it
      const instance = barcodeResult.instances[0];
      const item = barcodeResult.items[0];
      if (item) {
        toggleSelection(item.id, instance.id);
        toast.success(t`Added: ${barcodeResult.product.name}`);
      }
    } else {
      // Multiple instances - show instance picker
      const item = barcodeResult.items[0];
      if (item) {
        setPickerItem(item);
        setPickerInstances(barcodeResult.instances);
        setShowInstancePicker(true);
      }
    }
    setScannedBarcode('');
  }, [scannedBarcode, barcodeFetching, barcodeResult, toggleSelection, addPendingItem]);

  // Handle product search result - for unknown barcodes
  useEffect(() => {
    if (!productSearchBarcode || productSearchFetching) return;

    // Create pending item from search result
    const localProduct = productSearchResult?.local?.[0];
    const externalProduct = productSearchResult?.external?.[0];
    const product = localProduct || externalProduct;

    if (product) {
      // Found product info - create pending item with details
      addPendingItem({
        barcode: productSearchBarcode,
        productId: localProduct?.id,
        productName: product.name,
        productBrand: product.brand || undefined,
        productImageUrl: product.imageUrl || undefined,
        quantity: product.defaultQuantity || 1,
        unit: product.defaultUnit || 'item',
      });
      toast.success(t`${product.name} added - select storage location`);
    } else {
      // No product found - create pending item with just barcode
      addPendingItem({
        barcode: productSearchBarcode,
        quantity: 1,
        unit: 'item',
      });
      toast.info(t`Unknown barcode added - enter item details`);
    }

    setProductSearchBarcode('');
  }, [productSearchBarcode, productSearchResult, productSearchFetching, addPendingItem]);

  const isLoading = householdLoading;

  /**
   * Handle barcode scan from continuous scanner.
   */
  const handleBarcodeScan = useCallback(async (barcode: string): Promise<boolean> => {
    setScannedBarcode(barcode);
    return true;
  }, []);

  /**
   * Handle item selection from inventory picker.
   */
  const handleSelectItem = useCallback(
    (item: ItemWithInstanceCounts, instances: ItemInstance[]) => {
      setShowInventoryPicker(false);

      if (instances.length === 1) {
        // Single instance - auto-select it
        toggleSelection(item.id, instances[0].id);
        toast.success(t`Added: ${item.customName || item.product?.name || t`Item`}`);
      } else {
        // Multiple instances - show instance picker
        setPickerItem(item);
        setPickerInstances(instances);
        setShowInstancePicker(true);
      }
    },
    [toggleSelection, t]
  );

  /**
   * Handle instance selection from instance picker.
   */
  const handleSelectInstance = useCallback(
    (instance: ItemInstance) => {
      if (pickerItem) {
        toggleSelection(pickerItem.id, instance.id);
        toast.success(t`Added: ${pickerItem.customName || pickerItem.product?.name || t`Item`}`);
      }
      setShowInstancePicker(false);
      setPickerItem(null);
      setPickerInstances([]);
    },
    [pickerItem, toggleSelection, t]
  );

  /**
   * Remove a selected item.
   */
  const handleRemoveItem = useCallback(
    (instanceId: string) => {
      const itemId = getItemIdForInstance(instanceId);
      if (itemId) {
        toggleSelection(itemId, instanceId);
      }
    },
    [getItemIdForInstance, toggleSelection]
  );

  /**
   * Start the consume/waste flow.
   * Initializes quantities to the full amount for each selected item.
   */
  const handleStartAction = useCallback(
    (type: 'consumed' | 'wasted') => {
      setActionType(type);
      setSelectedReason(null);
      setError(null);
      setConfirmSubStep('adjust-quantities');

      // Initialize quantities, display units, and max quantities for each selected item
      const quantities = new Map<string, number>();
      const units = new Map<string, ItemUnit>();
      const maxQtys = new Map<string, number>();

      for (const selected of selectedItems) {
        const id = selected.instance.id;
        const qty = selected.instance.quantity;
        const unit = selected.item.unit;
        quantities.set(id, qty);
        units.set(id, unit);
        maxQtys.set(id, qty);
      }
      // For pending items, use their quantityToUse (or full quantity if not set)
      for (const pending of pendingItems) {
        const qtyToUse = pending.quantityToUse ?? pending.quantity;
        quantities.set(pending.clientId, qtyToUse);
        units.set(pending.clientId, pending.unit);
        maxQtys.set(pending.clientId, qtyToUse);
      }

      setItemQuantities(quantities);
      setDisplayUnits(units);
      setMaxInDisplayUnits(maxQtys);

      setStep('confirm-action');
    },
    [selectedItems, pendingItems]
  );

  /**
   * Submit the bulk update.
   * If there are pending items, creates them first, then marks all items consumed/wasted.
   */
  const handleSubmit = useCallback(async () => {
    if (!selectedReason || !actionType) {
      setError(t`Please select a reason`);
      return;
    }

    // Validate pending items have required fields
    if (pendingItemsCount > 0 && !areAllPendingItemsValid()) {
      setError(t`All new items need a name and storage location`);
      return;
    }

    setStep('processing');
    setError(null);

    try {
      // Step 1: Create pending items first (if any)
      const createdItems: Array<{ itemId: string; instanceId: string }> = [];

      if (pendingItemsCount > 0) {
        const createPayload: BulkCreateItem[] = pendingItems.map((p) => ({
          productId: p.productId,
          barcode: p.barcode, // Allow API to find/create product from OFF if productId missing
          customName: p.customName,
          storageLocationId: p.storageLocationId,
          quantity: p.quantity,
          unit: p.unit,
          expiryDate: p.expiryDate,
        }));

        const createResult = await bulkCreate.mutateAsync({
          householdId,
          data: { items: createPayload, createdFrom: 'web' },
        });

        // Collect successfully created items for the update step
        for (const result of createResult.results) {
          if (result.success && result.itemId && result.instanceId) {
            createdItems.push({
              itemId: result.itemId,
              instanceId: result.instanceId,
            });
          }
        }

        if (createResult.failed > 0) {
          logger.warn(`${createResult.failed} items failed to create`, {
            failed: createResult.failed,
          });
        }
      }

      // Step 2: Build updates from selected items + newly created items
      const updates: BulkUpdateItem[] = [];

      // Add existing selected items with adjusted quantities
      for (const selected of selectedItems) {
        const id = selected.instance.id;
        const adjustedQty = itemQuantities.get(id);
        const originalQty = selected.instance.quantity;
        const storedUnit = selected.item.unit;
        const displayUnit = displayUnits.get(id) ?? storedUnit;
        const maxInDisplay = maxInDisplayUnits.get(id) ?? originalQty;

        // Check if using full amount (compare in display unit with tolerance for float precision)
        const isUsingAll =
          adjustedQty === undefined || Math.abs(adjustedQty - maxInDisplay) < 0.001;

        // Convert quantity back to stored unit if user switched units
        let finalQuantity = adjustedQty ?? maxInDisplay;
        const unitCategory = getUnitCategory(storedUnit);
        if (displayUnit !== storedUnit && unitCategory !== 'discrete') {
          const converted = convertQuantity(finalQuantity, displayUnit, storedUnit);
          if (converted !== null) {
            finalQuantity = Math.round(converted * 1000) / 1000;
          }
        }

        updates.push({
          itemId: selected.item.id,
          instanceId: id,
          status: actionType,
          // Only include quantityToConsume if not using entire instance
          ...(!isUsingAll ? { quantityToConsume: finalQuantity } : {}),
          ...(actionType === 'consumed'
            ? { consumeReason: selectedReason as ConsumeReason }
            : { wasteReason: selectedReason as WasteReason }),
        });
      }

      // Add newly created items with adjusted quantities
      for (let i = 0; i < createdItems.length; i++) {
        const created = createdItems[i];
        const pendingItem = pendingItems[i];
        if (!pendingItem) continue;

        const id = pendingItem.clientId;
        const adjustedQty = itemQuantities.get(id);
        const fullQuantity = pendingItem.quantity; // Full amount added to inventory
        const quantityToUse = pendingItem.quantityToUse ?? fullQuantity; // Amount user wants to use
        const storedUnit = pendingItem.unit;
        const displayUnit = displayUnits.get(id) ?? storedUnit;

        // Convert quantity back to stored unit if user switched units
        let finalQuantity = adjustedQty ?? quantityToUse;
        const unitCategory = getUnitCategory(storedUnit);
        if (displayUnit !== storedUnit && unitCategory !== 'discrete') {
          const converted = convertQuantity(finalQuantity, displayUnit, storedUnit);
          if (converted !== null) {
            finalQuantity = Math.round(converted * 1000) / 1000;
          }
        }

        // For pending items, compare against full quantity since item was created with that amount
        // If using less than full amount, we need to pass quantityToConsume
        const isUsingFullAmount = Math.abs(finalQuantity - fullQuantity) < 0.001;

        updates.push({
          itemId: created.itemId,
          instanceId: created.instanceId,
          status: actionType,
          ...(!isUsingFullAmount ? { quantityToConsume: finalQuantity } : {}),
          ...(actionType === 'consumed'
            ? { consumeReason: selectedReason as ConsumeReason }
            : { wasteReason: selectedReason as WasteReason }),
        });
      }

      // Step 3: Bulk update all items
      const result = await bulkUpdate.mutateAsync({
        householdId,
        data: { updates },
      });

      if (result.failed > 0) {
        toast.warning(t`${result.successful} items processed, ${result.failed} failed`);
      } else {
        const statusText = actionType === 'consumed' ? t`consumed` : t`wasted`;
        toast.success(t`${result.successful} items marked as ${statusText}`);
      }

      // Track meal prep completed
      const durationSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
      track(AnalyticsEvents.MEAL_PREP_COMPLETED, {
        household_id: householdId,
        item_count: totalCount,
        items_consumed: actionType === 'consumed' ? result.successful : 0,
        items_wasted: actionType === 'wasted' ? result.successful : 0,
        duration_seconds: durationSeconds,
      });

      setStep('success');
      // Auto-close after showing success
      autoCloseTimerRef.current = setTimeout(() => {
        deactivate();
        navigate({ to: '/items' });
      }, 1500);
    } catch (err) {
      logger.error('Bulk operation failed', err, {
        householdId: selectedHousehold?.id,
        actionType,
        itemCount: selectedItems.length,
      });
      setError(err instanceof Error ? err.message : t`Failed to process items`);
      setStep('confirm-action');
    }
  }, [
    selectedReason,
    actionType,
    selectedItems,
    bulkUpdate,
    bulkCreate,
    householdId,
    deactivate,
    navigate,
    pendingItems,
    pendingItemsCount,
    areAllPendingItemsValid,
    itemQuantities,
    displayUnits,
    maxInDisplayUnits,
    selectedHousehold?.id,
    t,
    totalCount,
    track,
  ]);

  /**
   * Discard the session.
   */
  const handleDiscard = () => {
    const count = getSelectionCount();
    if (count > 0) {
      if (!confirm(t`Discard ${count} selected items?`)) {
        return;
      }
    }
    deactivate();
    navigate({ to: '/items' });
  };

  const handleStartMiniTour = async (tourId: MealPrepMiniTourId) => {
    if (startingMiniTourId) return;

    setStartingMiniTourId(tourId);
    try {
      await resetTour.mutateAsync({ tourId, deviceType: 'web' });
      await startTour(tourId);
    } catch (tourError) {
      console.error(`Failed to start meal prep mini tour ${tourId}:`, tourError);
    } finally {
      setStartingMiniTourId(null);
    }
  };

  // Show empty state if no household selected
  if (!householdLoading && !selectedHousehold) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center animate-in fade-in duration-500">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="relative mb-6">
            <div className="w-24 h-24 mx-auto rounded-3xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
              <HomeIcon className="h-12 w-12 text-primary" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
              <BeakerIcon className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-foreground mb-2">
            <Trans>Meal Prep</Trans>
          </h1>
          <p className="text-muted-foreground mb-6">
            <Trans>
              Select a household to start meal prep and batch process your pantry items.
            </Trans>
          </p>
          <Button asChild className="rounded-xl px-6">
            <Link to="/households">
              <Trans>Manage Households</Trans>
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  // Loading state for first load
  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center animate-in fade-in duration-300">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full bg-primary/10 animate-ping" />
            <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
              <BeakerIcon className="h-8 w-8 text-primary animate-pulse" />
            </div>
          </div>
          <p className="text-muted-foreground font-medium">
            <Trans>Loading meal prep...</Trans>
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (step === 'success') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center animate-in zoom-in-95 fade-in duration-500">
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div
              className="absolute inset-0 rounded-full bg-fresh/20 animate-ping"
              style={{ animationDuration: '1.5s' }}
            />
            <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-fresh/30 to-fresh/10 flex items-center justify-center">
              <CheckCircleIcon className="h-12 w-12 text-fresh" />
            </div>
          </div>
          <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
            <Trans>All Done!</Trans>
          </h2>
          <p className="text-muted-foreground">
            <Trans>Redirecting to inventory...</Trans>
          </p>
        </div>
      </div>
    );
  }

  // Processing state
  if (step === 'processing') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center animate-in fade-in duration-300">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
            <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            <div className="absolute inset-3 rounded-full bg-gradient-to-br from-primary/20 to-transparent flex items-center justify-center">
              <BeakerIcon className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-1">
            <Trans>Processing Items</Trans>
          </h2>
          <p className="text-muted-foreground text-sm">
            <Trans>This won&apos;t take long...</Trans>
          </p>
        </div>
      </div>
    );
  }

  // Confirm action step - two sub-steps: adjust quantities, then select reason
  if (step === 'confirm-action' && actionType) {
    const reasons = actionType === 'consumed' ? CONSUME_REASONS : WASTE_REASONS;
    const labels = actionType === 'consumed' ? CONSUME_REASON_LABELS : WASTE_REASON_LABELS;
    const reasonTitle = actionType === 'consumed' ? t`How were these used?` : t`What happened?`;
    const accentColor = actionType === 'consumed' ? 'fresh' : 'expired';
    const getReasonIcon = actionType === 'consumed' ? getConsumeReasonIcon : getWasteReasonIcon;

    // Helper to update quantity for an item using unit-appropriate step sizes
    const handleQuantityAdjust = (id: string, delta: number, max: number, unit: ItemUnit) => {
      setItemQuantities((prev) => {
        const next = new Map(prev);
        const current = next.get(id) ?? max;
        const step = getQuantityStepSize(unit);
        const newVal = Math.round((current + delta * step) * 1000) / 1000;
        next.set(id, Math.max(step, Math.min(max, newVal)));
        return next;
      });
    };

    // Helper to set exact quantity for an item
    const handleQuantitySet = (id: string, value: number, max: number) => {
      setItemQuantities((prev) => {
        const next = new Map(prev);
        next.set(id, Math.max(0.01, Math.min(max, value)));
        return next;
      });
    };

    // Handle unit change with quantity conversion
    const handleUnitChange = (id: string, newUnit: ItemUnit, baseUnit: ItemUnit) => {
      const currentUnit = displayUnits.get(id) ?? baseUnit;
      if (newUnit === currentUnit) return;

      // Convert the current quantity to the new unit
      const currentQty = itemQuantities.get(id);
      if (currentQty !== undefined) {
        const convertedQty = convertQuantity(currentQty, currentUnit, newUnit);
        if (convertedQty !== null) {
          setItemQuantities((prev) => {
            const next = new Map(prev);
            next.set(id, Math.round(convertedQty * 1000) / 1000);
            return next;
          });
        }
      }

      // Convert the max quantity to the new unit
      const currentMax = maxInDisplayUnits.get(id);
      if (currentMax !== undefined) {
        const convertedMax = convertQuantity(currentMax, currentUnit, newUnit);
        if (convertedMax !== null) {
          setMaxInDisplayUnits((prev) => {
            const next = new Map(prev);
            next.set(id, Math.round(convertedMax * 1000) / 1000);
            return next;
          });
        }
      }

      // Update the display unit
      setDisplayUnits((prev) => {
        const next = new Map(prev);
        next.set(id, newUnit);
        return next;
      });
    };

    // Back button handler - go to previous step or back to selecting
    const handleBack = () => {
      if (confirmSubStep === 'select-reason') {
        setConfirmSubStep('adjust-quantities');
      } else {
        setStep('selecting');
      }
    };

    return (
      <div className="max-w-2xl mx-auto animate-in slide-in-from-right-4 fade-in duration-300">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleBack}
            className="flex items-center justify-center h-10 w-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-muted-foreground" />
          </button>
          <div className="flex items-center gap-3 flex-1">
            <div
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-xl',
                actionType === 'consumed' ? 'bg-fresh/10' : 'bg-expired/10'
              )}
            >
              {actionType === 'consumed' ? (
                <CheckCircleIcon className="h-6 w-6 text-fresh" />
              ) : (
                <TrashIcon className="h-6 w-6 text-expired" />
              )}
            </div>
            <div>
              <h1 className="font-heading text-xl font-semibold text-foreground">
                {actionType === 'consumed' ? (
                  <Trans>Mark as Used</Trans>
                ) : (
                  <Trans>Mark as Wasted</Trans>
                )}
              </h1>
              <p className="text-sm text-muted-foreground">
                {confirmSubStep === 'adjust-quantities' ? (
                  <Trans>Step 1: Adjust quantities</Trans>
                ) : (
                  <Trans>Step 2: Select reason</Trans>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center gap-2 mb-6">
          <div className={cn('flex-1 h-1.5 rounded-full transition-colors', 'bg-primary')} />
          <div
            className={cn(
              'flex-1 h-1.5 rounded-full transition-colors',
              confirmSubStep === 'select-reason' ? 'bg-primary' : 'bg-muted'
            )}
          />
        </div>

        {/* Sub-step 1: Adjust quantities */}
        {confirmSubStep === 'adjust-quantities' && (
          <div className="space-y-4 animate-in fade-in duration-200">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                <Trans>How much of each item?</Trans>
              </Label>
              <span className="text-xs text-muted-foreground">
                <Plural value={totalCount} one="# item" other="# items" />
              </span>
            </div>

            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
              {/* Existing selected items */}
              {selectedItems.map((selected, idx) => {
                const id = selected.instance.id;
                const baseUnit = selected.item.unit;
                const displayUnit = displayUnits.get(id) ?? baseUnit;
                const maxQty = maxInDisplayUnits.get(id) ?? selected.instance.quantity;
                const currentQty = itemQuantities.get(id) ?? maxQty;
                const step = getQuantityStepSize(displayUnit);
                const quickOptions = getQuickSelectOptions(displayUnit, maxQty);
                const canSwitchUnits = getUnitCategory(baseUnit) !== 'discrete';

                return (
                  <Card
                    key={id}
                    className="border-border/50 animate-in fade-in"
                    style={{ animationDelay: `${idx * 30}ms` }}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg overflow-hidden bg-muted">
                          {selected.item.product?.imageUrl ? (
                            <img
                              src={selected.item.product.imageUrl}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <ArchiveBoxIcon className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-sm block truncate">
                            {selected.item.customName || selected.item.product?.name || t`Item`}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            <Trans>Total: {formatQuantityDisplay(maxQty, displayUnit)}</Trans>
                          </span>
                        </div>
                      </div>

                      {/* Quantity controls */}
                      <div className="flex items-center justify-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleQuantityAdjust(id, -1, maxQty, displayUnit)}
                          disabled={currentQty <= step}
                          className="h-10 w-10 rounded-xl"
                        >
                          <MinusIcon className="h-4 w-4" />
                        </Button>

                        <div className="flex flex-col items-center min-w-[100px]">
                          <div className="flex items-center gap-1">
                            <span className="text-2xl font-bold text-foreground">
                              {formatQuantityDisplay(currentQty, displayUnit)}
                            </span>
                            {canSwitchUnits ? (
                              <UnitSelector
                                value={displayUnit}
                                onChange={(newUnit) => handleUnitChange(id, newUnit, baseUnit)}
                                baseUnit={baseUnit}
                              />
                            ) : (
                              <span className="text-sm text-muted-foreground px-2">
                                {UNIT_DISPLAY_LABELS[displayUnit]}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            <Trans>of {formatQuantityDisplay(maxQty, displayUnit)}</Trans>
                          </span>
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleQuantityAdjust(id, 1, maxQty, displayUnit)}
                          disabled={currentQty >= maxQty}
                          className="h-10 w-10 rounded-xl"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Quick select buttons - common cooking amounts */}
                      <div className="flex justify-center gap-2 mt-3 flex-wrap">
                        {quickOptions.map((option) => {
                          const isSelected = Math.abs(currentQty - option.value) < 0.001;
                          return (
                            <Button
                              key={option.label}
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleQuantitySet(id, option.value, maxQty)}
                              className={cn(
                                'rounded-lg min-w-[48px] text-xs',
                                isSelected &&
                                  `border-${accentColor} bg-${accentColor}/10 text-${accentColor}`
                              )}
                            >
                              {option.label}
                            </Button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Pending items (new items being created) */}
              {pendingItems.map((pending, idx) => {
                const id = pending.clientId;
                const baseUnit = pending.unit;
                const displayUnit = displayUnits.get(id) ?? baseUnit;
                const maxQty = maxInDisplayUnits.get(id) ?? pending.quantity;
                const currentQty = itemQuantities.get(id) ?? maxQty;
                const step = getQuantityStepSize(displayUnit);
                const quickOptions = getQuickSelectOptions(displayUnit, maxQty);
                const canSwitchUnits = getUnitCategory(baseUnit) !== 'discrete';

                return (
                  <Card
                    key={id}
                    className="border-border/50 animate-in fade-in"
                    style={{ animationDelay: `${(selectedItems.length + idx) * 30}ms` }}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg overflow-hidden bg-muted">
                          {pending.productImageUrl ? (
                            <img
                              src={pending.productImageUrl}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <ArchiveBoxIcon className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">
                              {pending.customName || pending.productName || t`New Item`}
                            </span>
                            <span className="text-xs text-primary bg-primary/10 px-1.5 py-0.5 rounded-full flex-shrink-0">
                              <Trans>New</Trans>
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            <Trans>Total: {formatQuantityDisplay(maxQty, displayUnit)}</Trans>
                          </span>
                        </div>
                      </div>

                      {/* Quantity controls */}
                      <div className="flex items-center justify-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleQuantityAdjust(id, -1, maxQty, displayUnit)}
                          disabled={currentQty <= step}
                          className="h-10 w-10 rounded-xl"
                        >
                          <MinusIcon className="h-4 w-4" />
                        </Button>

                        <div className="flex flex-col items-center min-w-[100px]">
                          <div className="flex items-center gap-1">
                            <span className="text-2xl font-bold text-foreground">
                              {formatQuantityDisplay(currentQty, displayUnit)}
                            </span>
                            {canSwitchUnits ? (
                              <UnitSelector
                                value={displayUnit}
                                onChange={(newUnit) => handleUnitChange(id, newUnit, baseUnit)}
                                baseUnit={baseUnit}
                              />
                            ) : (
                              <span className="text-sm text-muted-foreground px-2">
                                {UNIT_DISPLAY_LABELS[displayUnit]}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            <Trans>of {formatQuantityDisplay(maxQty, displayUnit)}</Trans>
                          </span>
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleQuantityAdjust(id, 1, maxQty, displayUnit)}
                          disabled={currentQty >= maxQty}
                          className="h-10 w-10 rounded-xl"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Quick select buttons - common cooking amounts */}
                      <div className="flex justify-center gap-2 mt-3 flex-wrap">
                        {quickOptions.map((option) => {
                          const isSelected = Math.abs(currentQty - option.value) < 0.001;
                          return (
                            <Button
                              key={option.label}
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleQuantitySet(id, option.value, maxQty)}
                              className={cn(
                                'rounded-lg min-w-[48px] text-xs',
                                isSelected &&
                                  `border-${accentColor} bg-${accentColor}/10 text-${accentColor}`
                              )}
                            >
                              {option.label}
                            </Button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Continue button */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="ghost"
                onClick={() => setStep('selecting')}
                className="rounded-xl flex-1 sm:flex-none"
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                onClick={() => setConfirmSubStep('select-reason')}
                className={cn(
                  'rounded-xl flex-1 text-white min-w-[160px]',
                  actionType === 'consumed'
                    ? 'bg-fresh hover:bg-fresh/90'
                    : 'bg-expired hover:bg-expired/90'
                )}
                size="lg"
              >
                <Trans>Continue</Trans>
                <ChevronRightIcon className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Sub-step 2: Select reason */}
        {confirmSubStep === 'select-reason' && (
          <div className="space-y-4 animate-in slide-in-from-right-4 fade-in duration-200">
            {/* Summary of quantities */}
            <Card className="mb-4 overflow-hidden border-border/50 bg-muted/30">
              <CardContent className="py-3">
                <div className="space-y-2">
                  {selectedItems.slice(0, 3).map((selected) => {
                    const id = selected.instance.id;
                    const baseUnit = selected.item.unit;
                    const displayUnit = displayUnits.get(id) ?? baseUnit;
                    const qty = itemQuantities.get(id) ?? selected.instance.quantity;
                    return (
                      <div key={id} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground truncate flex-1 mr-2">
                          {selected.item.customName || selected.item.product?.name || t`Item`}
                        </span>
                        <span className="font-medium text-foreground flex-shrink-0">
                          {formatQuantityDisplay(qty, displayUnit)}{' '}
                          {UNIT_DISPLAY_LABELS[displayUnit]}
                        </span>
                      </div>
                    );
                  })}
                  {pendingItems.slice(0, Math.max(0, 3 - selectedItems.length)).map((pending) => {
                    const id = pending.clientId;
                    const displayUnit = displayUnits.get(id) ?? pending.unit;
                    const qty = itemQuantities.get(id) ?? pending.quantity;
                    return (
                      <div key={id} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground truncate flex-1 mr-2">
                          {pending.customName || pending.productName || t`New Item`}
                        </span>
                        <span className="font-medium text-foreground flex-shrink-0">
                          {formatQuantityDisplay(qty, displayUnit)}{' '}
                          {UNIT_DISPLAY_LABELS[displayUnit]}
                        </span>
                      </div>
                    );
                  })}
                  {totalCount > 3 && (
                    <p className="text-xs text-muted-foreground pt-1 border-t border-border/50">
                      <Trans>+{totalCount - 3} more</Trans>
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Reason selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">{reasonTitle}</Label>
              <div className="grid gap-2">
                {reasons.map((reason, index) => {
                  const Icon = getReasonIcon(reason as any);
                  const isSelected = selectedReason === reason;
                  return (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => setSelectedReason(reason as ConsumeReason | WasteReason)}
                      className={cn(
                        'flex items-center w-full px-4 py-3 rounded-xl border text-left transition-all duration-200 animate-in fade-in',
                        isSelected
                          ? `border-${accentColor} bg-${accentColor}/5 shadow-sm`
                          : 'border-border/50 bg-card hover:bg-muted/50 hover:border-border'
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-lg mr-3 transition-colors',
                          isSelected ? `bg-${accentColor}/20` : 'bg-muted'
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-5 w-5 transition-colors',
                            isSelected ? `text-${accentColor}` : 'text-muted-foreground'
                          )}
                        />
                      </div>
                      <span
                        className={cn(
                          'flex-1 font-medium transition-colors',
                          isSelected ? 'text-foreground' : 'text-muted-foreground'
                        )}
                      >
                        {labels[reason as keyof typeof labels]}
                      </span>
                      {isSelected && (
                        <div
                          className={cn(
                            'h-6 w-6 rounded-full flex items-center justify-center animate-in zoom-in duration-200',
                            `bg-${accentColor}`
                          )}
                        >
                          <svg
                            className="h-4 w-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-4 animate-in slide-in-from-top-2">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="ghost"
                onClick={() => setConfirmSubStep('adjust-quantities')}
                className="rounded-xl flex-1 sm:flex-none"
              >
                <Trans>Back</Trans>
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!selectedReason || bulkUpdate.isPending}
                className={cn(
                  'rounded-xl flex-1 text-white min-w-[160px]',
                  actionType === 'consumed'
                    ? 'bg-fresh hover:bg-fresh/90'
                    : 'bg-expired hover:bg-expired/90'
                )}
                size="lg"
              >
                {bulkUpdate.isPending ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    <Trans>Processing...</Trans>
                  </div>
                ) : actionType === 'consumed' ? (
                  <>
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    <Trans>Mark as Used</Trans>
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-5 w-5 mr-2" />
                    <Trans>Mark as Wasted</Trans>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="space-y-6 pb-36 animate-in fade-in duration-300"
      data-tour="meal-prep-page-intro"
    >
      {/* Page header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <button
            onClick={handleDiscard}
            className="flex items-center justify-center h-10 w-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors flex-shrink-0"
          >
            <ArrowLeftIcon className="h-5 w-5 text-muted-foreground" />
          </button>
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex-shrink-0">
              <BeakerIcon className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0">
              <h1 className="font-heading text-xl sm:text-2xl font-semibold text-foreground">
                <Trans>Meal Prep</Trans>
              </h1>
              <p className="text-sm text-muted-foreground hidden sm:block">
                <Trans>Select items you&apos;re using for cooking</Trans>
              </p>
            </div>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-tour="meal-prep-help"
              aria-label={t`Open meal prep tour help`}
              title={t`Meal prep help`}
              aria-busy={startingMiniTourId !== null}
              disabled={startingMiniTourId !== null}
              className="flex-shrink-0 gap-1.5 rounded-xl"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
              <span className="hidden sm:inline">
                <Trans>Help</Trans>
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              onSelect={() => {
                void handleStartMiniTour('meal-prep-basics');
              }}
            >
              <Trans>Meal prep basics tour</Trans>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                void handleStartMiniTour('meal-prep-actions');
              }}
            >
              <Trans>Used vs wasted actions tour</Trans>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Quick action buttons */}
      <div className="flex gap-3" data-tour="meal-prep-quick-actions">
        <Button
          onClick={() => setShowScanner(true)}
          className="flex-1 sm:flex-none rounded-xl"
          data-tour="meal-prep-open-scanner"
        >
          <QrCodeIcon className="h-5 w-5 mr-2" />
          <Trans>Open Scanner</Trans>
        </Button>
        <Button
          onClick={() => setShowInventoryPicker(true)}
          variant="outline"
          className="flex-1 sm:flex-none rounded-xl"
          data-tour="meal-prep-pick-list"
        >
          <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
          <Trans>Pick from List</Trans>
        </Button>
      </div>

      {/* Selected items */}
      <div className="space-y-4" data-tour="meal-prep-selected-items">
        {/* Loading indicator for product search */}
        {productSearchFetching && (
          <div className="rounded-xl bg-muted/30 border border-border/50 p-4 flex items-center gap-3 animate-in fade-in duration-200">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent flex-shrink-0" />
            <p className="text-sm text-muted-foreground">
              <Trans>Looking up product...</Trans>
            </p>
          </div>
        )}

        {/* Instruction banner when no items selected */}
        {totalCount === 0 && (
          <div
            className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-6 animate-in fade-in slide-in-from-bottom-2 duration-500"
            data-tour="meal-prep-instruction"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20">
                <QrCodeIcon className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-foreground mb-1">
                  <Trans>Scan items to add them</Trans>
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <Trans>
                    Use the barcode scanner or browse your inventory to select items for batch
                    processing. Items not in your inventory will be added automatically.
                  </Trans>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Demo preview for actions tour when no real items are selected */}
        {shouldShowGhostMealPrepItems && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <SparklesIcon className="h-4 w-4 text-primary" />
              </div>
              <h2 className="font-heading font-medium text-foreground">
                <Trans>Demo Selection</Trans>
              </h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {DEMO_MEAL_PREP_ITEMS.map((item, idx) => (
                <Card
                  key={item.id}
                  className="border-border/50 animate-in fade-in slide-in-from-bottom-2"
                  style={{ animationDelay: `${idx * 30}ms` }}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-muted">
                      <ArchiveBoxIcon className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm truncate text-foreground">{item.name}</h4>
                      <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-md inline-block mt-1">
                        {item.quantity}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Pending items (new items to be created) */}
        {pendingItems.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <SparklesIcon className="h-4 w-4 text-primary" />
              </div>
              <h2 className="font-heading font-medium text-foreground">
                <Trans>New Items</Trans>
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({pendingItemsCount})
                </span>
              </h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {pendingItems.map((pendingItem, idx) => (
                <div
                  key={pendingItem.clientId}
                  className="animate-in fade-in slide-in-from-bottom-2"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <PendingItemCard
                    item={pendingItem}
                    householdId={householdId}
                    onUpdate={(updates) => updatePendingItem(pendingItem.clientId, updates)}
                    onRemove={() => removePendingItem(pendingItem.clientId)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected items list from inventory */}
        {selectedItems.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-fresh/10">
                  <ArchiveBoxIcon className="h-4 w-4 text-fresh" />
                </div>
                <h2 className="font-heading font-medium text-foreground">
                  <Trans>From Inventory</Trans>
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    ({selectionCount})
                  </span>
                </h2>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="text-muted-foreground hover:text-foreground rounded-lg"
              >
                <Trans>Clear All</Trans>
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {selectedItems.map((selected, idx) => (
                <div
                  key={selected.instance.id}
                  className="animate-in fade-in slide-in-from-bottom-2"
                  style={{ animationDelay: `${idx * 30}ms` }}
                >
                  <SelectedItemCard
                    selected={selected}
                    onRemove={() => handleRemoveItem(selected.instance.id)}
                    household={selectedHousehold}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Continuous scanner */}
      {showScanner && (
        <ContinuousBarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
          itemCount={totalCount}
        />
      )}

      {/* Inventory picker sheet */}
      <InventoryPickerSheet
        isOpen={showInventoryPicker}
        onClose={() => setShowInventoryPicker(false)}
        householdId={householdId}
        onSelectItem={handleSelectItem}
      />

      {/* Instance picker sheet */}
      <InstancePickerSheet
        isOpen={showInstancePicker}
        onClose={() => {
          setShowInstancePicker(false);
          setPickerItem(null);
          setPickerInstances([]);
        }}
        item={pickerItem}
        instances={pickerInstances}
        onSelectInstance={handleSelectInstance}
      />

      {/* Fixed bottom action bar */}
      {(totalCount > 0 || shouldShowGhostActionBar) && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 animate-in slide-in-from-bottom-4 duration-300"
          data-tour="meal-prep-action-bar"
        >
          <div className="bg-gradient-to-t from-background via-background to-transparent pt-6 pb-4 px-4">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-2xl bg-card border border-border/50 shadow-xl p-4">
                <div className="flex items-center gap-4">
                  {/* Item count badge */}
                  <div className="flex items-center gap-2 bg-muted/50 px-3 py-2 rounded-xl flex-shrink-0">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/20">
                      <span className="text-sm font-bold text-primary">
                        {displayActionBarCount}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-foreground font-medium">
                        <Plural value={displayActionBarCount} one="item" other="items" />
                      </span>
                      {pendingItemsCount > 0 && (
                        <span className="text-muted-foreground ml-1">
                          ({t`${pendingItemsCount} new`})
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-3 flex-1 justify-end">
                    <Button
                      onClick={() => handleStartAction('consumed')}
                      size="lg"
                      className="flex-1 sm:flex-none rounded-xl bg-fresh hover:bg-fresh/90 text-white min-w-[120px]"
                      data-tour="meal-prep-used-button"
                      disabled={shouldShowGhostActionBar}
                    >
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      <Trans>Used</Trans>
                    </Button>
                    <Button
                      onClick={() => handleStartAction('wasted')}
                      variant="outline"
                      size="lg"
                      className="flex-1 sm:flex-none rounded-xl border-expired/30 text-expired hover:bg-expired/10 hover:text-expired min-w-[120px]"
                      data-tour="meal-prep-wasted-button"
                      disabled={shouldShowGhostActionBar}
                    >
                      <TrashIcon className="h-5 w-5 mr-2" />
                      <Trans>Wasted</Trans>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
